import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { LIFETIME_MEMBER_LABEL } from '../../utils/membership.util';

@Component({
  selector: 'app-form-renew-member',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgbTooltipModule
  ],
  templateUrl: './form-renew-member.component.html',
  styleUrl: './form-renew-member.component.scss'
})
export class FormRenewMemberComponent implements OnInit, OnDestroy {
  @Input() userId: string = ''; // User ID passed from parent component

  renewForm!: FormGroup;
  currentStep = 1;
  totalSteps = 2;
  isSubmitting = false;

  // Fee rate per year (ค่าธรรมเนียม 500 บาท/ปี + VAT 7% = 535 บาท/ปี)
  readonly FEE_PER_YEAR = 535;

  // ค่าธรรมเนียมตลอดชีพ 5,000 บาท (ยังไม่รวม VAT)
  readonly LIFETIME_FEE_BEFORE_VAT = 5000;
  readonly VAT_RATE = 0.07;

  /** ค่าธรรมเนียมตลอดชีพรวม VAT แล้ว (5,000 + 7% = 5,350) */
  readonly LIFETIME_FEE = Math.round(this.LIFETIME_FEE_BEFORE_VAT * (1 + this.VAT_RATE));

  // ประเภทการต่ออายุ
  readonly RENEW_TYPE_YEARLY = 'รายปี';
  readonly RENEW_TYPE_LIFETIME = LIFETIME_MEMBER_LABEL;

  renewTypes = [
    {
      value: 'รายปี',
      label: 'ต่ออายุรายปี (ค่าธรรมเนียม 500 บาทต่อปี)',
      description: 'ระบุจำนวนปีที่ต้องการต่ออายุ'
    },
    {
      value: LIFETIME_MEMBER_LABEL,
      label: 'ตลอดชีพ (ค่าธรรมเนียม 5,000 บาทตลอดชีพ)',
      description: 'ไม่มีวันหมดอายุ ไม่ต้องระบุจำนวนปี'
    }
  ];

  /** ผู้ใช้เลือกต่ออายุแบบตลอดชีพหรือไม่ */
  get isLifetimeRenewal(): boolean {
    return this.renewForm?.get('renewType')?.value === this.RENEW_TYPE_LIFETIME;
  }

  // Computed fee getter: ตลอดชีพ = 5,350 | รายปี = 535 × renewYears
  get calculatedFee(): number {
    if (this.isLifetimeRenewal) {
      return this.LIFETIME_FEE;
    }

    const years = Number(this.renewForm?.get('renewYears')?.value) || 0;
    return years > 0 ? this.FEE_PER_YEAR * years : 0;
  }

  // File upload tracking
  uploadedFiles: { [key: string]: File | null } = {};

  private subscription = new Subscription();

  // Dropdown options
  documentTypes = [
    { value: '1', label: '1. แบบไฟล์ รูปถ่าย 1 นิ้ว หน้าตรง สำหรับ ทำบัตร:' }
  ];

  constructor(
    private fb: FormBuilder,
    public activeModal: NgbActiveModal,
    private http: HttpClient
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    this.loadUserData();

    // Reactively update feeAmount whenever renewYears changes
    this.subscription.add(
      this.renewForm.get('renewYears')!.valueChanges.subscribe(() => {
        this.renewForm.get('feeAmount')?.setValue(this.calculatedFee, { emitEvent: false });
      })
    );

    // เปลี่ยนประเภทการต่ออายุ: ตลอดชีพไม่ต้องกรอกจำนวนปี
    this.subscription.add(
      this.renewForm.get('renewType')!.valueChanges.subscribe(() => {
        this.applyRenewTypeState();
        this.renewForm.get('feeAmount')?.setValue(this.calculatedFee, { emitEvent: false });
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Initialize the reactive form
   */
  private initForm(): void {
    this.renewForm = this.fb.group({
      // Step 1: Renewal Request (คำขอต่ออายุ)
      renewType: ['รายปี', Validators.required],
      renewYears: ['', [Validators.required, Validators.min(1)]],
      feeAmount: [0], // Computed dynamically: ตลอดชีพ = 5,350 | รายปี = 535 × renewYears
      requiredDocuments: [[]],

      // Step 2: User Information (Read-Only - loaded from user profile)
      titleName: [''],
      firstName: [''],
      lastName: [''],
      idCard: [''],
      dateOfBirth: [''],
      nationality: [''],
      phoneNumber: [''],
      lineId: [''],
      email: [''],

      // Current Address (Read-Only)
      currentHouseNumber: [''],
      currentVillage: [''],
      currentSoi: [''],
      currentRoad: [''],
      currentSubdistrict: [''],
      currentDistrict: [''],
      currentProvince: [''],
      currentPostalCode: [''],

      // Registered Address (Read-Only)
      registeredHouseNumber: [''],
      registeredVillage: [''],
      registeredSoi: [''],
      registeredRoad: [''],
      registeredSubdistrict: [''],
      registeredDistrict: [''],
      registeredProvince: [''],
      registeredPostalCode: [''],
      sameAsRegistered: [false]
    });
  }

  /**
   * Load user data (mock for now)
   */
  private loadUserData(): void {
    // TODO: Load actual user data from service
    // For now, using mock data
    const mockUserData = {
      titleName: 'นาย',
      firstName: 'ข้อมูลถูกดึงจากคอนทรีบิวเตอร์รันเกา',
      lastName: 'ข้อมูลถูกดึงจากคอนทรีบิวเตอร์รันเกา',
      idCard: '1234567890123',
      dateOfBirth: '1990-01-01',
      nationality: 'ไทย',
      phoneNumber: '0812345678',
      lineId: 'mylineid',
      email: 'example@email.com',

      // Registered Address
      registeredHouseNumber: '123',
      registeredVillage: 'หมู่ 1',
      registeredSoi: 'ซอย',
      registeredRoad: 'ถนน',
      registeredSubdistrict: 'ตำบล/แขวง',
      registeredDistrict: 'อำเภอ/เขต',
      registeredProvince: 'จังหวัด',
      registeredPostalCode: '12345',

      // Current Address
      currentHouseNumber: '123',
      currentVillage: 'หมู่ 1',
      currentSoi: 'ซอย',
      currentRoad: 'ถนน',
      currentSubdistrict: 'ตำบล/แขวง',
      currentDistrict: 'อำเภอ/เขต',
      currentProvince: 'จังหวัด',
      currentPostalCode: '12345',
      sameAsRegistered: false
    };

    // Populate form with user data
    this.renewForm.patchValue(mockUserData, { emitEvent: false });

    // Disable all personal info fields to make them read-only
    this.disablePersonalInfoFields();
  }

  /**
   * Disable all personal information fields to make them read-only
   */
  private disablePersonalInfoFields(): void {
    const personalInfoFields = [
      'titleName', 'firstName', 'lastName', 'idCard', 'dateOfBirth', 'nationality',
      'phoneNumber', 'lineId', 'email',
      'registeredHouseNumber', 'registeredVillage', 'registeredSoi', 'registeredRoad',
      'registeredSubdistrict', 'registeredDistrict', 'registeredProvince', 'registeredPostalCode',
      'currentHouseNumber', 'currentVillage', 'currentSoi', 'currentRoad',
      'currentSubdistrict', 'currentDistrict', 'currentProvince', 'currentPostalCode',
      'sameAsRegistered'
    ];

    personalInfoFields.forEach(field => {
      this.renewForm.get(field)?.disable({ emitEvent: false });
    });
  }

  /**
   * Navigate to next step
   */
  nextStep(): void {
    if (this.validateCurrentStep()) {
      if (this.currentStep < this.totalSteps) {
        this.currentStep++;
      }
    }
  }

  /**
   * Navigate to previous step
   */
  previousStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  /**
   * Go to specific step
   */
  goToStep(step: number): void {
    if (step >= 1 && step <= this.totalSteps) {
      // Validate all previous steps
      let canProceed = true;
      for (let i = 1; i < step; i++) {
        if (!this.validateStep(i)) {
          canProceed = false;
          break;
        }
      }

      if (canProceed) {
        this.currentStep = step;
      }
    }
  }

  /**
   * ปรับเงื่อนไขช่องจำนวนปีตามประเภทการต่ออายุ
   * - ตลอดชีพ: ไม่ต้องกรอกจำนวนปี (ล้างค่าและตัด validator ออก)
   * - รายปี: ต้องกรอกจำนวนปีอย่างน้อย 1 ปี
   */
  private applyRenewTypeState(): void {
    const renewYearsControl = this.renewForm.get('renewYears');
    if (!renewYearsControl) {
      return;
    }

    if (this.isLifetimeRenewal) {
      renewYearsControl.clearValidators();
      renewYearsControl.setValue('', { emitEvent: false });
    } else {
      renewYearsControl.setValidators([Validators.required, Validators.min(1)]);
    }

    renewYearsControl.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Validate current step
   */
  private validateCurrentStep(): boolean {
    return this.validateStep(this.currentStep);
  }

  /**
   * Validate specific step
   */
  private validateStep(step: number): boolean {
    switch (step) {
      case 1:
        // ตลอดชีพไม่ต้องกรอกจำนวนปี
        if (this.isLifetimeRenewal) {
          return true;
        }

        // Validate renewal years
        const renewYears = this.renewForm.get('renewYears');
        return !!renewYears?.valid;
      case 2:
        // Step 2 is read-only, always valid
        return true;
      default:
        return false;
    }
  }

  /**
   * Check if step is completed
   */
  isStepCompleted(step: number): boolean {
    return this.validateStep(step);
  }

  /**
   * Handle file upload
   */
  onFileSelect(event: Event, documentType: string): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        alert('ไฟล์มีขนาดใหญ่เกินไป กรุณาเลือกไฟล์ที่มีขนาดไม่เกิน 5MB');
        input.value = '';
        return;
      }

      // Validate file type (PDF, images)
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        alert('กรุณาเลือกไฟล์ประเภท PDF, JPG, JPEG หรือ PNG เท่านั้น');
        input.value = '';
        return;
      }

      this.uploadedFiles[documentType] = file;
      console.log(`File uploaded for ${documentType}:`, file.name);
    }
  }

  /**
   * Remove uploaded file
   */
  removeFile(documentType: string): void {
    this.uploadedFiles[documentType] = null;
    // Reset the file input
    const fileInput = document.getElementById(`file-${documentType}`) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  /**
   * Get file size in readable format
   */
  getFileSize(documentKey: string): string {
    const file = this.uploadedFiles[documentKey];
    if (!file) return '';

    const bytes = file.size;
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  /**
   * Get uploaded file name
   */
  getFileName(documentType: string): string {
    return this.uploadedFiles[documentType]?.name || '';
  }

  /**
   * Check if file is uploaded
   */
  hasFile(documentType: string): boolean {
    return !!this.uploadedFiles[documentType];
  }

  /**
   * Get full address string
   */
  getFullAddress(type: 'registered' | 'current'): string {
    const prefix = type === 'registered' ? 'registered' : 'current';
    const parts = [
      this.renewForm.get(`${prefix}HouseNumber`)?.value,
      this.renewForm.get(`${prefix}Village`)?.value,
      this.renewForm.get(`${prefix}Soi`)?.value,
      this.renewForm.get(`${prefix}Road`)?.value,
      this.renewForm.get(`${prefix}Subdistrict`)?.value,
      this.renewForm.get(`${prefix}District`)?.value,
      this.renewForm.get(`${prefix}Province`)?.value,
      this.renewForm.get(`${prefix}PostalCode`)?.value
    ].filter(part => part && part.trim() !== '');

    return parts.join(' ') || '-';
  }

  /**
   * Submit the form
   */
  onSubmit(): void {
    if (this.renewForm.valid) {
      this.isSubmitting = true;

      // Check if userId is provided
      if (!this.userId) {
        alert('⚠️ ไม่พบข้อมูลผู้ใช้ กรุณาเข้าสู่ระบบใหม่');
        this.isSubmitting = false;
        return;
      }

      // Prepare FormData for file upload
      const formData = new FormData();

      // Add userId
      formData.append('userId', this.userId);

      // Add work experience years (จำนวนปีที่ต้องการต่ออายุ) — ตลอดชีพส่ง 0
      const renewYears = this.isLifetimeRenewal ? 0 : this.renewForm.get('renewYears')?.value;
      formData.append('workExperienceYears', renewYears.toString());

      // ประเภทการต่ออายุ (รายปี / ตลอดชีพ)
      formData.append('membershipType', this.renewForm.get('renewType')?.value);

      // Get computed fee amount (535 × renewYears)
      const feeAmount = this.calculatedFee;
      formData.append('feeAmount', feeAmount.toString());

      // Add files to FormData and collect document names
      Object.keys(this.uploadedFiles).forEach(key => {
        const file = this.uploadedFiles[key];
        if (file) {
          formData.append('files', file);

          // Find the document label for this key
          const docType = this.documentTypes.find(d => d.value === key);
          if (docType) {
            // Extract the label without the number prefix
            const label = docType.label.substring(docType.label.indexOf('.') + 1).trim();
            // Append each document name individually
            formData.append('documentNames', label);
          }
        }
      });

      console.log('📤 Submitting renewal form to API');
      console.log('User ID:', this.userId);
      console.log('Work Experience Years:', renewYears);
      console.log('Fee Amount:', feeAmount);

      // Send POST request to API
      const apiUrl = `${environment.apiUrl}/form-renew-member`;

      this.http.post(apiUrl, formData).subscribe({
        next: (response: any) => {
          console.log('✅ Renewal request submitted successfully:', response);
          this.isSubmitting = false;

          alert('✅ ส่งคำขอต่ออายุสมาชิกเรียบร้อยแล้ว');

          // Close modal with success result and response data
          this.activeModal.close({
            success: true,
            data: response
          });
        },
        error: (error) => {
          console.error('❌ Error submitting renewal request:', error);
          this.isSubmitting = false;

          const errorMessage = error.error?.message || 'เกิดข้อผิดพลาดในการส่งคำขอ กรุณาลองใหม่อีกครั้ง';
          alert(`⚠️ เกิดข้อผิดพลาด: ${errorMessage}`);
        }
      });
    } else {
      alert('⚠️ กรุณากรอกข้อมูลให้ครบถ้วน');
      this.markFormGroupTouched(this.renewForm);
    }
  }

  /**
   * Mark all fields as touched to show validation errors
   */
  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  /**
   * Close the modal
   */
  close(): void {
    this.activeModal.dismiss('cancel');
  }

  /**
   * Get progress percentage
   */
  get progressPercentage(): number {
    return (this.currentStep / this.totalSteps) * 100;
  }
}
